/** Provides color utility functions and constants for task contexts and UI elements. */
import { isOverdueDate } from '@/features/tasks/utils/views/taskTimeUtils';

const ISO_DATE_LENGTH = 10;

const localDateStr = (date = new Date()) => date.toLocaleDateString('en-CA');

const taskDatePart = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return null;
  return dateStr.length >= ISO_DATE_LENGTH
    ? dateStr.slice(0, ISO_DATE_LENGTH)
    : dateStr;
};

const CATEGORICAL_COLORS = [
  'var(--color-context-indigo)',
  'var(--color-context-fuchsia)',
  'var(--color-context-orange)',
  'var(--color-context-teal)',
  'var(--color-context-lime)',
  'var(--color-context-emerald)',
  'var(--color-context-cyan)',
  'var(--color-context-pink)',
];

export { getProjectColor } from '@/utils/colorUtils';

// WCAG relative-luminance threshold: above this a background is light enough
// that black text gives better contrast than white. See WCAG 2.x 1.4.3.
const CONTRAST_LUMINANCE_THRESHOLD = 0.179;

const HEX_CHANNEL_MAX = 255;
const SRGB_LINEAR_THRESHOLD = 0.03928;
const SRGB_LINEAR_DIVISOR = 12.92;
const SRGB_OFFSET = 0.055;
const SRGB_SCALE = 1.055;
const SRGB_GAMMA = 2.4;
const HEX_PAIR_LENGTH = 6;
const HEX_RADIX = 16;
const WCAG_CONTRAST_OFFSET = 0.05;
const LUMA_R_COEFF = 0.2126;
const LUMA_G_COEFF = 0.7152;
const LUMA_B_COEFF = 0.0722;

const hexChannelToLinear = (channel) => {
  const value = channel / HEX_CHANNEL_MAX;
  return value <= SRGB_LINEAR_THRESHOLD
    ? value / SRGB_LINEAR_DIVISOR
    : Math.pow((value + SRGB_OFFSET) / SRGB_SCALE, SRGB_GAMMA);
};

/**
 * Returns the WCAG relative luminance (0..1) of a hex color.
 * @param {string} hex - '#rgb' or '#rrggbb' hex color.
 * @returns {number|null} Luminance, or null when the color cannot be parsed.
 */
export const getRelativeLuminance = (hex) => {
  const m =
    typeof hex === 'string' ? hex.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i) : null;
  if (!m) return null;
  const full =
    m[1].length === 3
      ? m[1]
          .split('')
          .map((c) => c + c)
          .join('')
      : m[1];
  const r = parseInt(full.slice(0, 2), HEX_RADIX);
  const g = parseInt(full.slice(2, 4), HEX_RADIX);
  const b = parseInt(full.slice(4, HEX_PAIR_LENGTH), HEX_RADIX);
  const lr = hexChannelToLinear(r);
  const lg = hexChannelToLinear(g);
  const lb = hexChannelToLinear(b);
  return LUMA_R_COEFF * lr + LUMA_G_COEFF * lg + LUMA_B_COEFF * lb;
};

const parseHexRgb = (hex) => {
  const m =
    typeof hex === 'string' ? hex.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i) : null;
  if (!m) return null;
  const full =
    m[1].length === 3
      ? m[1]
          .split('')
          .map((c) => c + c)
          .join('')
      : m[1];
  return {
    r: parseInt(full.slice(0, 2), HEX_RADIX),
    g: parseInt(full.slice(2, 4), HEX_RADIX),
    b: parseInt(full.slice(4, HEX_PAIR_LENGTH), HEX_RADIX),
  };
};

/** WCAG 2.x contrast ratio between two sRGB hex colors (1..21). */
export const getContrastRatio = (foregroundHex, backgroundHex) => {
  const lFg = getRelativeLuminance(foregroundHex);
  const lBg = getRelativeLuminance(backgroundHex);
  if (lFg === null || lBg === null) return null;
  const lighter = Math.max(lFg, lBg);
  const darker = Math.min(lFg, lBg);
  return (lighter + WCAG_CONTRAST_OFFSET) / (darker + WCAG_CONTRAST_OFFSET);
};

/**
 * Foreground at `opacity` over an opaque background (alpha compositing).
 * @returns {string|null} `#rrggbb`
 */
export const blendHexOnBackground = (foregroundHex, backgroundHex, opacity) => {
  const fg = parseHexRgb(foregroundHex);
  const bg = parseHexRgb(backgroundHex);
  if (!fg || !bg) return null;
  const a = opacity;
  const channel = (f, b) => Math.round(f * a + b * (1 - a));
  const r = channel(fg.r, bg.r);
  const g = channel(fg.g, bg.g);
  const b = channel(fg.b, bg.b);
  return `#${[r, g, b]
    .map((c) => c.toString(HEX_RADIX).padStart(2, '0'))
    .join('')}`;
};

/**
 * Chooses a readable text color (white/black) for a given background color
 * so the text meets WCAG AA contrast (4.5:1 for normal text).
 * @param {string} bgColor - Hex color, 'transparent', or a CSS var() token.
 * @returns {string} 'white' or 'black'.
 */
export const getContrastTextColor = (bgColor) => {
  const luminance = getRelativeLuminance(bgColor);
  if (luminance === null) return 'white'; // unparseable (transparent / var()) -> keep light text
  return luminance > CONTRAST_LUMINANCE_THRESHOLD ? 'black' : 'white';
};

const stringToColor = (str, colorArray) => {
  if (typeof str !== 'string' || !str) {
    return 'transparent';
  }
  const charCodeSum = str
    .split('')
    .reduce((sum, char) => sum + char.charCodeAt(0), 0);
  const colorIndex = charCodeSum % colorArray.length;
  return colorArray[colorIndex];
};

export const getContextColor = (contexts) => {
  if (!contexts || contexts.length === 0) {
    return 'transparent';
  }
  // Always use the first context for consistent color
  const firstContext = Array.isArray(contexts) ? contexts[0] : contexts;
  return stringToColor(firstContext, CATEGORICAL_COLORS);
};

const CAPACITY_WARNING_THRESHOLD = 0.8;

/**
 * Determines the color for a capacity indicator based on planned vs. max hours.
 * Returns a theme token string to use with MUI sx, or null for default grey styling.
 * @param {number} planned - Planned duration in minutes
 * @param {number} max - Maximum capacity in minutes
 * @returns {string|null} Theme token ('error.main', 'warning.main') or null for default
 */
export const getCapacityColor = (planned, max) => {
  if (planned > max) {
    return 'error.main';
  } else if (planned > max * CAPACITY_WARNING_THRESHOLD) {
    return 'warning.main';
  }
  return null;
};

/**
 * Determines the urgency color for a task based on its due date.
 * Returns a theme token string to use with MUI sx, or null for default styling.
 * @param {string} dateStr - Date string in YYYY-MM-DD format.
 * @returns {string|null} Theme token ('error.main' for overdue, 'warning.main' for tomorrow) or null for today and later future dates
 */
export const getUrgencyColor = (dateStr) => {
  const datePart = taskDatePart(dateStr);
  if (!datePart) return null;

  if (isOverdueDate(datePart)) {
    return 'error.main';
  }

  const tomorrowDate = new Date();
  tomorrowDate.setDate(tomorrowDate.getDate() + 1);
  const tomorrowDateStr = localDateStr(tomorrowDate);

  if (datePart === tomorrowDateStr) {
    return 'warning.main';
  }
  return null;
};
